import type { AuthUser } from '../../core/state/auth-store';
import { mountDashboard } from './sections/admin-dashboard';
import { mountUsers } from './sections/admin-users';
import { mountTransactions } from './sections/admin-transactions';
import { mountInvoices } from './sections/admin-invoices';
import { mountCmsHub } from './sections/admin-cms-hub';
import { mountSettings } from './sections/admin-settings-section';
import { mountMeta } from './sections/admin-meta';
import { mountApiKeys } from './sections/admin-api-keys';
import { mountPayments } from './sections/admin-payments';
import { mountWithdrawals } from './sections/admin-withdrawals';

type AdminSection =
  | 'dashboard' | 'users' | 'transactions' | 'invoices'
  | 'cms'
  | 'payments' | 'withdrawals' | 'settings' | 'meta' | 'api-keys';

interface NavItem { id: AdminSection; label: string; icon: string }

const NAV: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', icon: '&#9670;' },
  { id: 'users', label: 'Players', icon: '&#9823;' },
  { id: 'transactions', label: 'Transactions', icon: '&#8942;' },
  { id: 'invoices', label: 'Invoices', icon: '&#9633;' },
  { id: 'cms', label: 'CMS', icon: '&#167;' },
  { id: 'payments', label: 'Payments', icon: '&#128179;' },
  { id: 'withdrawals', label: 'Payouts & KYC', icon: '&#9998;' },
  { id: 'settings', label: 'Settings', icon: '&#9881;' },
  { id: 'meta', label: 'SEO / Meta', icon: '&#9776;' },
  { id: 'api-keys', label: 'API Keys', icon: '&#128273;' },
];

function navMarkup(active: AdminSection, collapsed: boolean): string {
  const items = NAV.map(n => `
    <button data-admin-nav="${n.id}"
      class="flex items-center gap-2.5 w-full min-h-11 px-3 rounded text-left text-[13px] transition-colors
        ${n.id === active ? 'bg-gold/15 text-gold font-semibold' : 'text-text hover:bg-surface-2'}"
      aria-current="${n.id === active ? 'page' : 'false'}">
      <span class="text-base leading-none ${collapsed ? '' : 'mr-0.5'}">${n.icon}</span>
      ${collapsed ? '' : `<span>${n.label}</span>`}
    </button>`).join('');

  return `
    <nav id="admin-sidebar" class="flex flex-col gap-0.5 ${collapsed ? 'w-14' : 'w-52'} shrink-0 p-2 bg-surface border-r border-border overflow-y-auto transition-[width] duration-200">
      <div class="flex items-center justify-between px-2 mb-2 min-h-11">
        ${collapsed ? '' : '<span class="font-heading text-gold text-sm tracking-wider uppercase">Admin</span>'}
        <button id="admin-sidebar-toggle" class="min-w-[44px] min-h-[44px] flex items-center justify-center text-muted hover:text-text">
          ${collapsed ? '&#9654;' : '&#9664;'}
        </button>
      </div>
      ${items}
      <div class="mt-auto pt-2 border-t border-border">
        <button id="admin-back-to-game" class="flex items-center gap-2.5 w-full min-h-11 px-3 rounded text-[13px] text-muted hover:text-text hover:bg-surface-2">
          <span class="text-base leading-none">&#8592;</span>
          ${collapsed ? '' : '<span>Back to Game</span>'}
        </button>
      </div>
    </nav>`;
}

function shellMarkup(active: AdminSection, collapsed: boolean): string {
  return `
    <div id="admin-shell" class="flex h-screen bg-bg text-text overflow-hidden">
      ${navMarkup(active, collapsed)}
      <main id="admin-content" class="flex-1 overflow-y-auto p-4 sm:p-6">
        <div id="admin-section-body" class="max-w-6xl mx-auto"></div>
      </main>
    </div>`;
}

export function mountAdminPanel(
  root: HTMLElement,
  _user: AuthUser,
  onExit: () => void,
): () => void {
  let active: AdminSection = 'dashboard';
  // Starts folded on a phone: open, the rail took 208 of 375px and left the
  // section a column too narrow for its own forms.
  let collapsed = window.matchMedia?.('(max-width: 639px)').matches ?? false;
  let sectionTeardown: (() => void) | null = null;

  function render(): void {
    root.innerHTML = shellMarkup(active, collapsed);
    wireShell();
    void mountSection();
  }

  async function mountSection(): Promise<void> {
    sectionTeardown?.();
    sectionTeardown = null;
    const body = root.querySelector<HTMLElement>('#admin-section-body');
    if (!body) return;
    body.innerHTML = '<p class="text-muted text-center py-12">Loading&hellip;</p>';

    try {
      switch (active) {
        case 'dashboard': sectionTeardown = await mountDashboard(body); break;
        case 'users': sectionTeardown = await mountUsers(body); break;
        case 'transactions': sectionTeardown = await mountTransactions(body); break;
        case 'invoices': sectionTeardown = await mountInvoices(body); break;
        case 'cms': sectionTeardown = await mountCmsHub(body); break;
        case 'payments': sectionTeardown = await mountPayments(body); break;
        case 'withdrawals': sectionTeardown = await mountWithdrawals(body); break;
        case 'settings': sectionTeardown = await mountSettings(body); break;
        case 'meta': sectionTeardown = await mountMeta(body); break;
        case 'api-keys': sectionTeardown = await mountApiKeys(body); break;
      }
    } catch {
      body.innerHTML = '<p class="text-red-400 text-center py-12">Failed to load section.</p>';
    }
  }

  function wireShell(): void {
    root.querySelectorAll<HTMLButtonElement>('[data-admin-nav]').forEach(btn => {
      btn.addEventListener('click', () => {
        const section = btn.dataset.adminNav as AdminSection;
        if (section !== active) {
          active = section;
          render();
        }
      });
    });

    root.querySelector('#admin-sidebar-toggle')?.addEventListener('click', () => {
      collapsed = !collapsed;
      render();
    });

    root.querySelector('#admin-back-to-game')?.addEventListener('click', onExit);
  }

  // Once, not per render: `root` outlives every render, so registering this in
  // wireShell stacked one more listener on each navigation.
  const onNavigate = ((e: CustomEvent<{ section: AdminSection }>) => {
    if (e.detail.section !== active) {
      active = e.detail.section;
      render();
    }
  }) as EventListener;
  root.addEventListener('admin:navigate', onNavigate);

  render();

  return () => {
    root.removeEventListener('admin:navigate', onNavigate);
    sectionTeardown?.();
    sectionTeardown = null;
    root.innerHTML = '';
  };
}
